// Workspace — Constitutional Layout Craftsmanship Pass (LF-005-CRAFT, 2026-02-09).
//
// Reads the constitutional rules at the top of
// /app/memory/WORKSPACE_REFRESH_DESIGN.md before deciding any placement:
//   1. Property Photo is the fixed hero.
//   2. Rendering Workspace is the largest interactive area.
//   3. Render Library is permanently visible beneath the photo.
//   4. Nothing else consumes permanent screen real estate.
//
// Structure:
//   LEFT column   → Photo (upper) + Render Library (lower)
//   RIGHT column  → Surface Rail (top) + Rendering Workspace (fills remaining)
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Check, ChevronRight, Download, HelpCircle, Layers, Loader2,
  Palette, Paintbrush2, Plus, Search, X,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import InviteMyPainterDialog from "@/components/visualiser/InviteMyPainterDialog";
import AreaPickerEntryCard from "@/components/workspace/AreaPickerEntryCard";
import PartsOfYourHomePanel from "@/components/workspace/PartsOfYourHomePanel";
import EditingPanel from "@/components/workspace/EditingPanel";
import { prettyTitle as prettyProjectTitle } from "@/lib/displayHelpers";
import { searchPalette } from "@/utils/paintSearch";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// ─── Wheel-forward interaction helpers ─────────────────────────
// Locked design principle (2026-02): wheel anywhere over a scrollable panel
// should scroll it — the customer must never have to hunt for the "sweet
// spot" that catches the wheel. These helpers wrap that behaviour without
// stealing scroll from controls that genuinely own their own vertical scroll
// (textareas, contenteditable regions, and anything explicitly marked
// [data-scroll-owner]). Plain <input> boxes are NOT scroll owners — wheeling
// over them must still scroll the parent panel.
function isScrollOwner(el) {
  if (!el || !(el instanceof Element)) return false;
  return !!el.closest("textarea, [contenteditable=true], [data-scroll-owner]");
}
// Forward vertical wheel to a ref'd child that owns the real overflow.
function useWheelForwardY(ref) {
  return useCallback((e) => {
    if (isScrollOwner(e.target)) return;
    const el = ref.current;
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight) return;
    el.scrollTop += e.deltaY;
    e.preventDefault();
  }, [ref]);
}
// Forward vertical wheel into HORIZONTAL scroll on a strip (Render Library).
// Only intercepts when the wheel is genuinely vertical — trackpad two-finger
// horizontal swipes still work natively.
function useWheelForwardYToX(ref) {
  return useCallback((e) => {
    if (isScrollOwner(e.target)) return;
    if (Math.abs(e.deltaY) < Math.abs(e.deltaX)) return;
    const el = ref.current;
    if (!el) return;
    if (el.scrollWidth <= el.clientWidth) return;
    el.scrollLeft += e.deltaY;
    e.preventDefault();
  }, [ref]);
}

// Standard Exterior / Standard Interior — the five permanent design groups the
// homeowner sees in the Surface Rail. Each group is displayed as one row and
// maps to a single canonical surface slug on the backend (multi-surface
// fan-out is planned but out of scope for this craftsmanship pass).
const STANDARD_EXTERIOR = [
  { key: "walls",  label: "Walls",                canonical: "exterior_walls" },
  { key: "trim",   label: "Trim & Window Frames", canonical: "window_frames" },
  { key: "fascia", label: "Fascia & Gutters",     canonical: "fascias" },
  { key: "door",   label: "Front Door",           canonical: "entry_door" },
  { key: "roof",   label: "Roof",                 canonical: "roof", optional: true },
];
const STANDARD_INTERIOR = [
  { key: "walls",   label: "Walls",             canonical: "walls" },
  { key: "ceiling", label: "Ceiling",           canonical: "ceiling" },
  { key: "trim",    label: "Trim & Skirting",   canonical: "trims" },
  { key: "doors",   label: "Doors",             canonical: "doors" },
  { key: "feature", label: "Feature Wall",      canonical: "feature_walls", optional: true },
];

function scopeOf(project) {
  if (!project) return "exterior";
  if (project.project_scope === "interior") return "interior";
  if (project.project_scope === "exterior") return "exterior";
  const s = project.surface_default || "";
  return s.startsWith("exterior") || ["fascias", "roof", "fences"].includes(s)
    ? "exterior" : "interior";
}

function useBlobUrl(url, deps) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!url) { setSrc(null); return undefined; }
    let dead = false; let blobUrl;
    (async () => {
      try {
        const r = await api.get(url, { responseType: "blob" });
        if (dead) return;
        blobUrl = URL.createObjectURL(r.data);
        setSrc(blobUrl);
      } catch { /* swallow */ }
    })();
    return () => { dead = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return src;
}

// Locked design principle (2026-02): Download must be always-visible and
// naturally associated with the render — no floating overlays, no hover-only
// reveals. This helper fetches the composite as a blob (auth cookie in play)
// and triggers a client-side save with a friendly filename.
function safeRenderFilename(title, versionId) {
  const clean = (title || "colourmatch-render")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60) || "colourmatch-render";
  return `${clean}-${(versionId || "").slice(0, 8)}.jpg`;
}

async function downloadCompositeBlob(versionId, filename) {
  const r = await api.get(`/visualiser/versions/${versionId}/composite`, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export default function Workspace() {
  const { id } = useParams();
  const nav = useNavigate();
  const [urlParams, setUrlParams] = useSearchParams();
  // Founder-locked: returning from /colours pre-selects the picked colour but
  // does NOT auto-apply. Apply remains the customer's final action.
  const presetColour = useMemo(() => {
    const hex = urlParams.get("preset_hex");
    const name = urlParams.get("preset_name");
    return hex ? { hex, name: name || hex } : null;
  }, [urlParams]);
  useEffect(() => {
    if (!presetColour) return;
    // Consume the query params so a page reload doesn't re-preselect.
    const p = new URLSearchParams(urlParams);
    p.delete("preset_hex"); p.delete("preset_name");
    setUrlParams(p, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // LF-007-RETURN (2026-02-24, founder-locked): after Stripe checkout the
  // customer is returned STRAIGHT to /visualiser/p/{id}?unlock_session=…
  // We confirm the transaction with the backend (which flips the project
  // to unlocked=true), refresh the project, celebrate with a single toast,
  // and clear the query param. We NEVER auto-fire the render — Render
  // Scheme remains the customer's final explicit action.
  const [unlockConfirming, setUnlockConfirming] = useState(false);
  useEffect(() => {
    const sessionId = urlParams.get("unlock_session");
    if (!sessionId) return;
    let dead = false;
    setUnlockConfirming(true);
    (async () => {
      try {
        await api.get(`/payments/status/${sessionId}`);
        if (dead) return;
        toast.success("Payment successful — your project is unlocked.");
      } catch (e) {
        if (dead) return;
        toast.error(formatApiError(e?.response?.data?.detail) || "We couldn't confirm your payment. Please refresh.");
      } finally {
        if (dead) return;
        // Refresh the project so unlocked=true propagates immediately.
        try { await api.get(`/visualiser/projects/${id}`).then((r) => setProject(r.data)); } catch { /* ignore */ }
        setUnlockConfirming(false);
        // Consume the query param so a reload doesn't re-toast.
        const p = new URLSearchParams(urlParams);
        p.delete("unlock_session");
        setUrlParams(p, { replace: true });
      }
    })();
    return () => { dead = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { user } = useAuth();

  const [project, setProject] = useState(null);
  const [activePhotoId, setActivePhotoId] = useState(null);
  const [ws, setWs] = useState(null);
  const [versions, setVersions] = useState([]);
  const [previewVerId, setPreviewVerId] = useState(null);   // null = current
  const [activeSurface, setActiveSurface] = useState(null); // primary editing slug — kept for legacy consumers
  const [palette, setPalette] = useState({ families: [] });
  const [surfaceCatalogue, setSurfaceCatalogue] = useState([]);
  // Gate 2 · Surface-Based Editing (LOCKED 2026-02) — catalogue-driven areas.
  // The frontend never hard-codes area names or part lists (Implementation
  // Rule 2). `partsByArea` is fetched from GET /api/surfaces.
  const [partsByArea, setPartsByArea] = useState([]);
  const [activeArea, setActiveArea] = useState(() => {
    try { return localStorage.getItem(`cm.workspace.activeArea.${id}`) || null; }
    catch { return null; }
  });
  const [selectedParts, setSelectedParts] = useState([]);
  const [allSurfacesOpen, setAllSurfacesOpen] = useState(false);
  const [renderJob, setRenderJob] = useState(null);
  const [renderPolling, setRenderPolling] = useState(false);
  const [accessDenied, setAccessDenied] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  const scope = scopeOf(project);
  const standardGroups = scope === "exterior" ? STANDARD_EXTERIOR : STANDARD_INTERIOR;

  // Persist the chosen area per-project so returning customers skip the
  // area picker.
  useEffect(() => {
    if (!id) return;
    try {
      if (activeArea) localStorage.setItem(`cm.workspace.activeArea.${id}`, activeArea);
    } catch { /* localStorage unavailable */ }
  }, [id, activeArea]);

  // ─── Data loading ───────────────────────────────────────────────
  const loadProject = useCallback(async () => {
    try {
      const { data } = await api.get(`/visualiser/projects/${id}`);
      setProject(data);
      const photos = data.photos || [];
      setActivePhotoId((current) => {
        if (current && photos.find((p) => p.id === current)) return current;
        return photos[0]?.id || null;
      });
    } catch (e) {
      const s = e?.response?.status;
      if (s === 403) setAccessDenied("forbidden");
      else if (s === 404) setAccessDenied("missing");
      else toast.error("Could not load project");
    }
  }, [id]);

  const loadWorkingScheme = useCallback(async (photoId) => {
    if (!photoId) return;
    try {
      const { data } = await api.get(
        `/visualiser/projects/${id}/photos/${photoId}/working-scheme`,
      );
      setWs(data);
    } catch (e) {
      if (e?.response?.status !== 404) toast.error("Could not load scheme");
    }
  }, [id]);

  const loadVersions = useCallback(async (photoId) => {
    if (!photoId) return;
    try {
      const { data } = await api.get(
        `/visualiser/projects/${id}/photos/${photoId}/versions`,
      );
      setVersions(data || []);
    } catch { /* silent */ }
  }, [id]);

  // Download the composite JPG for a given saved version. Reused by both the
  // photo-header toolbar and the persistent per-tile download button in the
  // Render Library. Callers only render the affordance when unlocked.
  const onDownloadVersion = useCallback(async (verId) => {
    if (!verId) return;
    try {
      const filename = safeRenderFilename(project?.title, verId);
      await downloadCompositeBlob(verId, filename);
      toast.success("Render saved to your device");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't download this render");
    }
  }, [project?.title]);

  useEffect(() => { void loadProject(); }, [loadProject]);
  useEffect(() => {
    if (!activePhotoId) return;
    void loadWorkingScheme(activePhotoId);
    void loadVersions(activePhotoId);
  }, [activePhotoId, loadWorkingScheme, loadVersions]);

  useEffect(() => {
    api.get("/palette").then((r) => setPalette(r.data)).catch(() => {});
    axios.get(`${API_BASE}/api/surfaces`)
      .then((r) => {
        const raw = r.data?.groups || {};
        const flat = [];
        for (const cat of ["interior", "exterior"]) {
          for (const g of raw[cat] || []) flat.push({ ...g, category: cat });
        }
        setSurfaceCatalogue(flat);
        // Catalogue-driven areas (LOCKED Rule 2, 2026-02).
        setPartsByArea(Array.isArray(r.data?.parts_by_area) ? r.data.parts_by_area : []);
      })
      .catch(() => { setSurfaceCatalogue([]); setPartsByArea([]); });
  }, []);

  // Poll active render job — no cancel/supersede handling (see review notes).
  useEffect(() => {
    if (!ws?.active_render_job_id) { setRenderPolling(false); return undefined; }
    let dead = false;
    setRenderPolling(true);
    (async () => {
      // eslint-disable-next-line no-constant-condition
      while (!dead) {
        try {
          const { data } = await api.get(`/visualiser/render-jobs/${ws.active_render_job_id}`);
          if (dead) return;
          setRenderJob(data);
          if (data.status === "succeeded") {
            await Promise.all([loadWorkingScheme(activePhotoId), loadVersions(activePhotoId)]);
            setRenderPolling(false);
            toast.success("Your scheme is ready to see");
            return;
          }
          if (data.status === "failed" || data.status === "cancelled"
              || data.status === "superseded" || data.status === "superseded_after_success") {
            setRenderPolling(false);
            if (data.status === "failed") toast.error(`Render failed: ${data.error || ""}`);
            return;
          }
        } catch { /* ignore */ }
        await new Promise((r) => setTimeout(r, 2000));
      }
    })();
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws?.active_render_job_id, activePhotoId]);

  // ─── Derived state ──────────────────────────────────────────────
  const assignments = ws?.manual_assignments || {};
  const assignedSlugs = Object.keys(assignments);
  const standardCanonicalSet = useMemo(
    () => new Set(standardGroups.map((g) => g.canonical)),
    [standardGroups],
  );
  const extras = useMemo(
    () => assignedSlugs.filter((s) => !standardCanonicalSet.has(s)),
    [assignedSlugs, standardCanonicalSet],
  );

  const surfaceLabel = useCallback((slug) => {
    const std = standardGroups.find((g) => g.canonical === slug);
    if (std) return std.label;
    for (const g of surfaceCatalogue) {
      const it = (g.items || []).find((x) => x.slug === slug);
      if (it) return it.display_name;
    }
    return slug.split("_").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");
  }, [surfaceCatalogue, standardGroups]);

  const activeVersion = useMemo(
    () => versions.find((v) => v.id === (previewVerId || ws?.active_version_id)),
    [versions, previewVerId, ws?.active_version_id],
  );

  const compositeUrl = activeVersion?.id
    ? `/visualiser/versions/${activeVersion.id}/composite`
    : null;
  const compositeSrc = useBlobUrl(compositeUrl, [compositeUrl]);
  const originalSrc = useBlobUrl(
    activePhotoId ? `/visualiser/projects/${id}/photos/${activePhotoId}/image` : null,
    [id, activePhotoId],
  );

  // Auto-select the first uncoloured Standard group on first entry so the
  // workspace is never in an "empty" state (constitutional decision).
  useEffect(() => {
    if (activeSurface || !ws) return;
    const firstUnset = standardGroups.find((g) => !assignments[g.canonical]);
    setActiveSurface((firstUnset || standardGroups[0]).canonical);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws?.id]);

  // ─── Mutations ──────────────────────────────────────────────────
  const assignColour = async (slug, colour) => {
    if (!activePhotoId) return;
    try {
      const { data } = await api.put(
        `/visualiser/projects/${id}/photos/${activePhotoId}/working-scheme/surfaces/${slug}`,
        { color_hex: colour.hex, color_name: colour.name, library_code: colour.library_code || null },
      );
      setWs(data);
      toast.success(`${colour.name} applied to ${surfaceLabel(slug)}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't apply colour");
    }
  };

  const removeSurface = async (slug) => {
    try {
      const { data } = await api.delete(
        `/visualiser/projects/${id}/photos/${activePhotoId}/working-scheme/surfaces/${slug}`,
      );
      setWs(data);
      if (activeSurface === slug) setActiveSurface(standardGroups[0].canonical);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't remove");
    }
  };

  // LF-007-RENDER (2026-02-24, founder-locked): the "Render Scheme" button
  // must never be disabled once one or more parts are coloured. If the
  // project is still in free preview, clicking it starts the Stripe unlock
  // flow instead of silently doing nothing. Constitutional rule: the UI
  // must never claim a part is coloured while preventing rendering.
  const [unlockBusy, setUnlockBusy] = useState(false);
  const beginUnlock = useCallback(async () => {
    if (unlockBusy) return;
    setUnlockBusy(true);
    try {
      const { data } = await api.post("/payments/unlock/create-session", {
        project_id: id,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Couldn't start checkout. Please try again.");
        setUnlockBusy(false);
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't start checkout");
      setUnlockBusy(false);
    }
  }, [id, unlockBusy]);

  const renderScheme = async () => {
    if (assignedSlugs.length === 0) {
      toast.error("Pick a colour for at least one part of your home");
      return;
    }
    if (!project?.unlocked) {
      // Founder-locked (LF-007-RENDER): route straight to unlock instead of
      // toast-and-hope. Assignments are preserved server-side; the customer
      // returns to the same scheme after checkout.
      await beginUnlock();
      return;
    }
    try {
      const { data } = await api.post(
        `/visualiser/projects/${id}/photos/${activePhotoId}/working-scheme/render`,
      );
      // LF-008-RENDER (2026-02-24, founder-locked): the render endpoint is
      // idempotent. If this exact scheme has already been rendered, the
      // backend returns the pre-existing succeeded job immediately with the
      // saved `produced_version_id`. Previously we always showed a fleeting
      // "Painting your scheme…" toast and then did nothing — no polling
      // (because `ws.active_render_job_id` stays null for a finished job),
      // no new Saved Render, no centre-photo change. From the customer's
      // seat that read exactly as "click does nothing" — which was the P0
      // reported by the founder.
      //
      // Branch on `data.status`:
      //   • "succeeded"  → refresh versions, surface the saved render in the
      //                    centre photo, and TELL THE CUSTOMER (single
      //                    success toast) so the click never feels silent.
      //   • "queued" / "running" / etc. → real work is happening, fall
      //                    through to the polling flow.
      setRenderJob(data);
      if (data?.status === "succeeded" && data?.produced_version_id) {
        await Promise.all([
          loadWorkingScheme(activePhotoId),
          loadVersions(activePhotoId),
        ]);
        setPreviewVerId(data.produced_version_id);
        toast.success("You've already rendered this exact scheme — showing your saved render.");
        return;
      }
      await loadWorkingScheme(activePhotoId);
      toast.message("Painting your scheme…");
    } catch (e) {
      const st = e?.response?.status;
      const msg = formatApiError(e?.response?.data?.detail) || e.message;
      if (st === 402) await beginUnlock();
      else toast.error(msg);
    }
  };

  const restoreVersion = async (verId, action) => {
    try {
      const { data } = await api.post(`/visualiser/versions/${verId}/restore`, { action });
      setWs(data.working_scheme);
      setPreviewVerId(null);
      if (action === "render") toast.message("Restoring and repainting…");
      else toast.success("Colours restored");
      await loadVersions(activePhotoId);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't restore");
    }
  };

  // ─── Render guards ─────────────────────────────────────────────
  if (accessDenied) {
    return (
      <div className="flex-1 min-h-0 flex flex-col items-center justify-center p-8 text-center" data-testid="ws-access-denied">
        <div className="text-5xl mb-4">{accessDenied === "missing" ? "🔍" : "🔒"}</div>
        <h1 className="text-2xl font-bold text-brand-ink mb-2">
          {accessDenied === "missing" ? "Project not found" : "This project isn't yours"}
        </h1>
        <Link to="/projects" className="mt-6 bg-brand-navy text-white h-11 px-5 rounded-xl text-sm font-semibold inline-flex items-center">My projects</Link>
      </div>
    );
  }
  if (!project || !ws) {
    return (
      <div className="flex-1 min-h-0 flex items-center justify-center" data-testid="ws-loading">
        <Loader2 className="animate-spin text-brand-teal" size={28} />
      </div>
    );
  }

  const unlocked = !!project.unlocked;
  const viewingOld = previewVerId && ws.active_version_id && previewVerId !== ws.active_version_id;
  const currentColour = activeSurface ? assignments[activeSurface] : null;

  const activeVersionLabel = activeVersion ? versionLabel(activeVersion, versions) : null;

  return (
    <div className="flex-1 min-h-0 w-full flex flex-col overflow-hidden bg-brand-linen" data-testid="workspace-page">
      <AppHeader
        project={project}
        ws={ws}
        onBack={() => nav("/projects")}
        renderPolling={renderPolling}
        onRenderScheme={renderScheme}
        onHelp={() => setHelpOpen(true)}
        onInvitePainter={() => setInviteOpen(true)}
        unlocked={unlocked}
        unlockBusy={unlockBusy}
        assignedCount={assignedSlugs.length}
      />
      <InviteMyPainterDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        projectId={project?.id}
      />

      {/* Two constitutional regions. Left = Property + Library. Right = Workspace. */}
      <div className="flex-1 min-h-0 flex gap-4 p-4 overflow-hidden">
        {/* LEFT — Property zone (photo + library, stacked) */}
        <section className="flex-[1.1] min-w-0 min-h-0 flex flex-col gap-4" data-testid="property-zone">
          <PhotoCard
            src={compositeSrc || originalSrc}
            variant={compositeSrc ? "composite" : "original"}
            renderPolling={renderPolling}
            renderJob={renderJob}
            viewingOld={viewingOld}
            versionLabel={activeVersion && viewingOld ? versionLabel(activeVersion, versions) : null}
            headerVersionLabel={activeVersionLabel}
            unlocked={unlocked}
            activeVersionId={activeVersion?.id}
            onDownload={onDownloadVersion}
            onRestoreEdit={() => activeVersion && restoreVersion(activeVersion.id, "edit")}
            onRestoreRender={() => activeVersion && restoreVersion(activeVersion.id, "render")}
            onReturnCurrent={() => setPreviewVerId(null)}
          />
          <RenderLibrary
            versions={versions}
            activeVersionId={ws.active_version_id}
            previewVerId={previewVerId}
            onSelect={(vId) => setPreviewVerId(vId)}
            originalSrc={originalSrc}
            projectId={id}
            onDownload={unlocked ? onDownloadVersion : null}
          />
        </section>

        {/* RIGHT — Choose an area → Choose a part → Choose a colour → Apply → Render.
             Catalogue-driven: areas + parts come from GET /api/surfaces
             (Implementation Rule 2, LOCKED 2026-02).
             LF-006-SCROLL (2026-02-24, founder-locked): the right column is a
             SINGLE vertical scroll surface. No nested scrollers inside — the
             Parts / Editing / Studio panels grow to their natural height and
             the column itself scrolls. Native mouse-wheel + trackpad + drag
             scrollbar all work because we simply own our overflow instead of
             fighting a flex-crush against `PartsOfYourHomePanel`. */}
        <section
          className="flex-1 min-w-0 min-h-0 overflow-y-auto cm-scroll flex flex-col gap-4 pr-1"
          data-testid="rendering-workspace"
        >
          {!activeArea ? (
            <AreaPickerEntryCard
              areas={partsByArea}
              onPick={(slug) => {
                setActiveArea(slug);
                setSelectedParts([]);
              }}
            />
          ) : (
            <>
              <PartsOfYourHomePanel
                areas={partsByArea}
                activeArea={activeArea}
                onChangeArea={(next) => {
                  setActiveArea(next);
                  setSelectedParts([]);  // reset selection when switching areas
                  toast.message(`Switched to ${(partsByArea.find((a) => a.slug === next) || {}).display_name || "another area"}`);
                }}
                assignments={assignments}
                selectedParts={selectedParts}
                onToggleSelected={(slug) => {
                  setSelectedParts((prev) => prev.includes(slug)
                    ? prev.filter((s) => s !== slug)
                    : [...prev, slug]);
                  setActiveSurface(slug); // keep legacy consumer of activeSurface in sync
                }}
              />
              <EditingPanel
                selectedParts={selectedParts}
                activeArea={activeArea}
                areas={partsByArea}
                assignments={assignments}
                currentColour={selectedParts[0] ? assignments[selectedParts[0]] : null}
              />
              <ColourStudio
                activeSurface={selectedParts[0] || null}
                surfaceLabel={(slug) => {
                  const area = partsByArea.find((a) => a.slug === activeArea);
                  const p = area?.parts?.find((x) => x.slug === slug);
                  return p?.display_name || surfaceLabel(slug);
                }}
                currentColour={selectedParts[0] ? assignments[selectedParts[0]] : null}
                palette={palette}
                presetColour={presetColour}
                projectId={id}
                activeArea={activeArea}
                activePartSlug={selectedParts[0] || null}
                onApply={async (colour) => {
                  if (selectedParts.length === 0) {
                    toast.error("Tap a part of your home first.");
                    return;
                  }
                  const results = await Promise.allSettled(
                    selectedParts.map((slug) => assignColour(slug, colour)),
                  );
                  const failed = results
                    .map((r, i) => (r.status === "rejected" ? selectedParts[i] : null))
                    .filter(Boolean);
                  if (failed.length === 0) {
                    if (selectedParts.length === 1) {
                      toast.success(`${colour.color_name || "Colour"} applied.`);
                    } else {
                      toast.success(`${colour.color_name || "Colour"} applied to ${selectedParts.length} parts of your home.`);
                    }
                  } else {
                    toast.error(`Could not paint ${failed.length} part${failed.length === 1 ? "" : "s"}: ${failed.join(", ")}`);
                  }
                }}
                applyLabelOverride={
                  selectedParts.length === 0
                    ? "Tap a part of your home"
                    : selectedParts.length === 1
                      ? null  // ColourStudio uses its default "Apply <Colour> to <Part>"
                      : `Apply to ${selectedParts.length} parts of your home`
                }
              />
            </>
          )}
        </section>
      </div>

      {allSurfacesOpen && (
        <AllSurfacesModal
          catalogue={surfaceCatalogue}
          scope={scope}
          exclude={new Set(assignedSlugs)}
          onClose={() => setAllSurfacesOpen(false)}
          onPick={(slug) => {
            setActiveSurface(slug);
            setAllSurfacesOpen(false);
            toast.message(`${surfaceLabel(slug)} added to your scheme`);
          }}
        />
      )}
      {helpOpen && <HelpOverlay onClose={() => setHelpOpen(false)} />}
    </div>
  );
}

function versionLabel(v, versions) {
  if (!v) return "";
  const idx = versions.findIndex((x) => x.id === v.id);
  if (idx < 0) return "This version";
  return `Version ${versions.length - idx}`;
}

// ─── Header ─────────────────────────────────────────────────────
function AppHeader({ project, ws, onBack, renderPolling, onRenderScheme,
                     onHelp, onInvitePainter, unlocked, unlockBusy, assignedCount }) {
  // LF-007-RENDER (2026-02-24, founder-locked): the button is enabled the
  // moment there is something to render — even in Free preview. If the
  // project is locked, the same click starts checkout so the user is never
  // stuck between "colour applied" and "cannot render".
  const disabled = renderPolling || unlockBusy || assignedCount === 0;
  const label = renderPolling
    ? "Painting…"
    : unlockBusy
      ? "Opening checkout…"
      : (!unlocked ? "Unlock to render" : "Render Scheme");
  return (
    <header className="h-14 flex items-center gap-3 px-4 border-b border-brand-border bg-white shrink-0" data-testid="ws-header">
      <button onClick={onBack} data-testid="ws-back-btn"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-muted hover:text-brand-ink transition-colors">
        <ArrowLeft size={14} /> Projects
      </button>
      <div className="min-w-0 flex-1 flex items-center gap-3">
        <div className="w-px h-6 bg-brand-border" />
        <h1 className="text-sm font-bold text-brand-ink truncate max-w-xs font-serif" data-testid="ws-title">
          {prettyProjectTitle(project.title)}
        </h1>
        {!unlocked && (
          <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid="ws-free-preview-badge">
            Free preview
          </span>
        )}
      </div>

      <button
        onClick={onRenderScheme}
        disabled={disabled}
        data-testid="ws-render-scheme-btn"
        data-render-state={renderPolling ? "polling" : (assignedCount === 0 ? "empty" : (!unlocked ? "unlock" : "ready"))}
        title={
          assignedCount === 0
            ? "Pick a colour for at least one part of your home first"
            : (!unlocked ? "Free preview — unlock to render your scheme ($29.99 AUD)" : "Render your scheme")
        }
        className="inline-flex items-center gap-1.5 bg-brand-navy text-white h-9 px-4 rounded-full text-xs font-semibold hover:bg-brand-teal disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-navy"
      >
        {renderPolling ? <Loader2 className="animate-spin" size={13} /> : <Palette size={13} />}
        {label}
      </button>

      <Link to={`/painters?from_project=${encodeURIComponent(project.id)}`}
        data-testid="ws-browse-painters-cta"
        className="hidden md:inline-flex items-center gap-1.5 text-xs font-medium text-brand-muted hover:text-brand-teal transition-colors">
        Browse ColourMatch Painters
      </Link>

      <button onClick={onInvitePainter} data-testid="ws-invite-painter-cta"
        className="hidden md:inline-flex items-center gap-1.5 text-xs font-medium text-brand-muted hover:text-brand-teal transition-colors">
        <Paintbrush2 size={13} /> Invite my painter
      </button>

      <button onClick={onHelp} data-testid="ws-help-btn"
        className="w-8 h-8 rounded-full text-brand-muted hover:text-brand-ink hover:bg-brand-linen inline-flex items-center justify-center transition-colors">
        <HelpCircle size={15} />
      </button>
    </header>
  );
}

// ─── PhotoCard ──────────────────────────────────────────────────
// Constitutional Rule 1: hero, fixed, never overlaid with chrome.
// Download lives in the image header/toolbar so it feels like part of the
// render itself — never a floating overlay (locked design principle 2026-02).
function PhotoCard({ src, variant, renderPolling, renderJob, viewingOld, versionLabel: verLabel,
                     headerVersionLabel, unlocked, activeVersionId, onDownload,
                     onRestoreEdit, onRestoreRender, onReturnCurrent }) {
  const showDownloadToolbar = variant === "composite" && unlocked && !viewingOld && !renderPolling && !!activeVersionId;
  return (
    <div className="flex-[3] min-h-0 bg-white rounded-3xl shadow-soft border border-brand-border overflow-hidden flex flex-col" data-testid="ws-photo-card">
      {viewingOld && (
        <div className="bg-amber-50 border-b border-amber-200 px-5 py-2.5 flex items-center justify-between gap-3 text-xs shrink-0" data-testid="restore-bar">
          <span className="font-semibold text-amber-900">You&rsquo;re looking at {verLabel}</span>
          <div className="flex items-center gap-2">
            <button onClick={onRestoreEdit} data-testid="restore-edit-btn"
              className="h-8 px-3 rounded-lg bg-brand-navy text-white text-[11px] font-semibold hover:bg-brand-teal transition-colors">
              Restore colours &amp; continue
            </button>
            <button onClick={onRestoreRender} data-testid="restore-render-btn"
              className="h-8 px-3 rounded-lg border border-brand-navy text-brand-navy text-[11px] font-semibold hover:bg-brand-linen transition-colors">
              Restore and repaint
            </button>
            <button onClick={onReturnCurrent} data-testid="restore-return-btn"
              className="h-8 px-3 rounded-lg text-brand-muted text-[11px] font-semibold hover:bg-brand-linen transition-colors">
              Return to current
            </button>
          </div>
        </div>
      )}
      {renderPolling && renderJob && !viewingOld && (
        <div className="bg-brand-ink text-white px-5 py-2.5 text-xs flex items-center gap-3 shrink-0" data-testid="ws-render-status">
          <Loader2 className="animate-spin" size={14} />
          <span className="font-semibold">Painting your scheme</span>
          {renderJob.progress?.current_surface && (
            <span className="text-white/70 truncate">
              · {renderJob.progress.completed || 0} of {renderJob.progress.total || 0} surfaces
            </span>
          )}
        </div>
      )}
      {showDownloadToolbar && (
        <div className="bg-white border-b border-brand-border px-5 py-2 flex items-center justify-between gap-3 shrink-0" data-testid="photo-toolbar">
          <div className="min-w-0 flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-muted">Current render</span>
            {headerVersionLabel && (
              <span className="text-[11px] text-brand-ink font-serif truncate">· {headerVersionLabel}</span>
            )}
          </div>
          <button
            onClick={() => onDownload && onDownload(activeVersionId)}
            data-testid="photo-download-btn"
            title="Download this render"
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-brand-navy text-brand-navy text-[11px] font-semibold hover:bg-brand-navy hover:text-white transition-colors"
          >
            <Download size={12} /> Download
          </button>
        </div>
      )}
      <div className="flex-1 min-h-0 flex items-center justify-center p-4 bg-brand-linen/40" data-testid="ws-photo-frame">
        {src ? (
          <img
            src={src}
            alt="Your property"
            className="max-h-full max-w-full object-contain rounded-2xl"
            data-testid={variant === "composite" ? "ws-photo-composite" : "ws-photo-original"}
          />
        ) : (
          <Loader2 className="animate-spin text-brand-teal" size={28} />
        )}
      </div>
    </div>
  );
}

// ─── Render Library ─────────────────────────────────────────────
// Constitutional Rule 3: permanent, never a modal/drawer/tab.
function RenderLibrary({ versions, activeVersionId, previewVerId, onSelect, originalSrc, projectId, onDownload }) {
  const current = previewVerId || activeVersionId;
  const stripRef = useRef(null);
  const onWheel = useWheelForwardYToX(stripRef);
  return (
    <div
      className="flex-[1] min-h-[160px] bg-white rounded-3xl shadow-soft border border-brand-border overflow-hidden flex flex-col"
      data-testid="render-library"
      onWheel={onWheel}
    >
      <div className="px-5 pt-4 pb-2 flex items-center justify-between shrink-0">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-muted">Your saved renders</div>
          <p className="text-[11px] text-brand-muted mt-0.5">Click any version to preview it in place.</p>
        </div>
        <span className="text-[10px] text-brand-muted">{versions.length} version{versions.length === 1 ? "" : "s"}</span>
      </div>
      <div ref={stripRef} className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden cm-scroll">
        <ol className="h-full flex items-stretch gap-3 px-5 pb-4">
          {/* Original always first, distinct outline treatment */}
          <LibraryTile
            testId="library-tile-original"
            label="Original"
            caption="The way it was"
            src={originalSrc}
            outline
            active={!current || (!versions.length)}
            onClick={() => onSelect(null)}
          />
          {versions.map((v, idx) => {
            const num = versions.length - idx;
            const label = `Version ${num}`;
            const rendered = Object.keys(v.assignments_rendered || {}).length;
            const isActive = current === v.id;
            return (
              <LibraryTile
                key={v.id}
                testId={`library-tile-v${num}`}
                label={label}
                caption={`${rendered} surface${rendered === 1 ? "" : "s"}`}
                src={`${API_BASE}/api/visualiser/versions/${v.id}/composite`}
                active={isActive}
                onClick={() => onSelect(v.id)}
                onDownload={onDownload ? () => onDownload(v.id) : null}
              />
            );
          })}
        </ol>
      </div>
    </div>
  );
}

function LibraryTile({ label, caption, src, active, outline, onClick, onDownload, testId }) {
  return (
    <li className="shrink-0">
      <div className="relative h-full">
        <button onClick={onClick} data-testid={testId}
          className={`group h-full flex flex-col rounded-2xl overflow-hidden transition-all ${
            active
              ? "ring-2 ring-brand-teal shadow-navy"
              : "ring-1 ring-brand-border hover:ring-brand-ink/40 hover:shadow-soft"
          }`}
          style={{ width: "clamp(160px, 15vw, 220px)" }}
        >
          <div className={`flex-1 min-h-0 relative ${outline ? "bg-brand-linen" : "bg-brand-sand/40"}`}>
            {src ? (
              <img src={src} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Loader2 className="animate-spin text-brand-muted" size={18} />
              </div>
            )}
            {outline && (
              <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest bg-white/90 text-brand-ink">
                Original
              </span>
            )}
          </div>
          <div className={`px-3 py-2 text-left ${active ? "bg-brand-ink text-white" : "bg-white"}`}>
            <div className={`text-xs font-bold ${active ? "text-white" : "text-brand-ink"}`}>{label}</div>
            <div className={`text-[10px] ${active ? "text-white/70" : "text-brand-muted"}`}>{caption}</div>
          </div>
        </button>
        {/* Permanent, always-discoverable download control — never hover-only.
            Never rendered on the Original tile (nothing new to save). */}
        {onDownload && !outline && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDownload(); }}
            data-testid={`${testId}-download`}
            title="Download this render"
            aria-label={`Download ${label}`}
            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 backdrop-blur-sm text-brand-navy border border-brand-border shadow-sm hover:bg-brand-navy hover:text-white hover:border-brand-navy transition-colors inline-flex items-center justify-center"
          >
            <Download size={12} />
          </button>
        )}
      </div>
    </li>
  );
}

// ─── Surface Rail ───────────────────────────────────────────────
function SurfaceRail({ standardGroups, extras, assignments, activeSurface, onSelect,
                       onRemoveExtra, onOpenAll, surfaceLabel }) {
  return (
    <nav className="bg-white rounded-3xl shadow-soft border border-brand-border shrink-0" data-testid="surface-rail">
      <div className="px-5 pt-4 pb-2">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-muted">Your scheme</div>
      </div>
      <ul className="flex flex-wrap items-stretch gap-2 px-4 pb-4">
        {standardGroups.map((g) => (
          <SurfaceChip
            key={g.key}
            slug={g.canonical}
            label={g.label + (g.optional ? " (optional)" : "")}
            colour={assignments[g.canonical]}
            active={activeSurface === g.canonical}
            onClick={() => onSelect(g.canonical)}
          />
        ))}
        {extras.map((slug) => (
          <SurfaceChip
            key={slug}
            slug={slug}
            label={surfaceLabel(slug)}
            colour={assignments[slug]}
            active={activeSurface === slug}
            removable
            onClick={() => onSelect(slug)}
            onRemove={() => onRemoveExtra(slug)}
          />
        ))}
        <li>
          <button onClick={onOpenAll} data-testid="rail-open-all"
            className="h-full min-h-[68px] inline-flex items-center gap-1.5 px-4 rounded-2xl border border-dashed border-brand-border text-xs font-semibold text-brand-muted hover:text-brand-teal hover:border-brand-teal transition-colors">
            <Plus size={12} /> Advanced Surfaces
          </button>
        </li>
      </ul>
    </nav>
  );
}

function SurfaceChip({ slug, label, colour, active, removable, onClick, onRemove }) {
  return (
    <li>
      <button onClick={onClick} data-testid={`rail-chip-${slug}`}
        className={`group h-full min-h-[68px] flex items-center gap-3 px-3.5 py-2.5 rounded-2xl border transition-all ${
          active
            ? "border-brand-ink bg-brand-linen shadow-soft"
            : "border-brand-border bg-white hover:border-brand-ink/40 hover:shadow-soft"
        }`}
      >
        <div className="relative">
          <div className="w-8 h-8 rounded-full border-2 border-white shadow-soft"
               style={{ background: colour?.color_hex || "repeating-linear-gradient(45deg,#e7e3d8,#e7e3d8 4px,#f5f1e8 4px,#f5f1e8 8px)" }} />
          {colour && (
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-brand-teal text-white inline-flex items-center justify-center border-2 border-white">
              <Check size={9} strokeWidth={3} />
            </span>
          )}
        </div>
        <div className="text-left">
          <div className={`text-[12px] font-bold leading-tight ${active ? "text-brand-ink" : "text-brand-ink"}`}>{label}</div>
          <div className="text-[10px] text-brand-muted leading-tight">{colour?.color_name || "Not picked"}</div>
        </div>
        {removable && (
          <span onClick={(e) => { e.stopPropagation(); onRemove(); }}
            data-testid={`rail-remove-${slug}`}
            className="ml-1 w-5 h-5 rounded-full text-brand-muted opacity-0 group-hover:opacity-100 hover:bg-brand-sand hover:text-destructive inline-flex items-center justify-center transition-opacity">
            <X size={10} />
          </span>
        )}
      </button>
    </li>
  );
}

// ─── Colour temperature helpers (client-side classification) ───
function hexToHsl(hex) {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let s = 0, hue = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hue = ((g - b) / d + (g < b ? 6 : 0)); break;
      case g: hue = ((b - r) / d + 2); break;
      default: hue = ((r - g) / d + 4);
    }
    hue *= 60;
  }
  return { h: hue, s, l };
}
function classifyWarmth(hex) {
  const { h, s } = hexToHsl(hex);
  if (s < 0.08) return "neutral";
  if ((h >= 0 && h < 70) || h >= 330) return "warm";
  if (h >= 170 && h < 270) return "cool";
  return "neutral";
}
function classifyBrightness(hex) {
  const { l } = hexToHsl(hex);
  if (l >= 0.72) return "bright";
  if (l >= 0.4) return "mid";
  return "deep";
}

const RECOMMENDED_TAGS = {
  exterior_walls:       ["hamptons", "coastal", "modern", "heritage", "warm-white", "cream", "grey"],
  window_frames:        ["trim", "white", "linen", "hamptons"],
  fascias:              ["trim", "white", "charcoal", "heritage"],
  entry_door:           ["feature", "hamptons", "heritage", "bold"],
  roof:                 ["charcoal", "grey", "black", "heritage"],
  gutters:              ["trim", "charcoal", "grey"],
  downpipes:            ["trim", "charcoal", "grey"],
  garage_door:          ["neutral", "grey", "charcoal"],
  fences:               ["heritage", "charcoal", "timber"],
  walls:                ["warm-white", "cream", "modern", "hamptons"],
  ceiling:              ["cool-white", "white"],
  trims:                ["trim", "white", "linen"],
  doors:                ["neutral", "warm-white"],
  feature_walls:        ["feature", "bold", "modern"],
};

const FAVOURITES_STORAGE_KEY = "colourmatch:favourites";
function loadFavourites() {
  try {
    const raw = localStorage.getItem(FAVOURITES_STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.map((s) => String(s).toUpperCase()) : [];
  } catch { return []; }
}
function saveFavourites(list) {
  try { localStorage.setItem(FAVOURITES_STORAGE_KEY, JSON.stringify(list.map((s) => s.toUpperCase()))); } catch { /* ignore */ }
}

// ─── Colour Studio (compact) ───────────────────────────────────
// LF-007-STUDIO (2026-02-24, founder-locked): the in-workspace Studio is
// intentionally COMPACT — never an in-panel version of the /colours page.
//   • ~12 Recommended swatches shown by default (never 30+, never 1 041)
//   • Retained entry points: Search, Recommended, Favourites
//   • Advanced filters (family / warmth / brightness) live on /colours
//   • "Browse all colours →" is the doorway to the full 1 041-colour library
//   • Apply CTA sticks to the bottom of the studio card so it is never
//     buried beneath a swatch wall
const STUDIO_SWATCH_LIMIT = 12;

function ColourStudio({ activeSurface, surfaceLabel, currentColour, palette, onApply, applyLabelOverride, presetColour, projectId, activeArea, activePartSlug }) {
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("recommended"); // "recommended" | "favourites"
  const [favourites, setFavourites] = useState(loadFavourites);

  // Founder-locked: colours arriving from /colours pre-select but never
  // auto-apply. Once consumed, the preset is cleared upstream by clearing
  // the URL params on the Workspace side.
  useEffect(() => {
    if (presetColour?.hex) {
      setSelected({ name: presetColour.name || presetColour.hex, hex: presetColour.hex });
    }
  }, [presetColour?.hex, presetColour?.name]);

  // Reset selection & search when the customer switches surface.
  useEffect(() => {
    setSelected(null);
    setQ("");
    setView("recommended");
  }, [activeSurface]);

  const flat = useMemo(
    () => (palette.families || []).flatMap((f) =>
      (f.colours || f.colors || []).map((c) => ({ ...c, family: c.family || f.name })),
    ),
    [palette],
  );

  const totalLibraryCount = flat.length;

  const toggleFavourite = (hex) => {
    const h = hex.toUpperCase();
    setFavourites((prev) => {
      const next = prev.includes(h) ? prev.filter((x) => x !== h) : [...prev, h];
      saveFavourites(next);
      return next;
    });
  };

  const handleApply = async (colour) => {
    onApply(colour);
    try {
      await api.post("/palette/recent", { name: colour.name, hex: colour.hex });
    } catch { /* best-effort */ }
  };

  // Recommended picks scored against the active part-of-your-home tags.
  const recommended = useMemo(() => {
    if (!activeSurface) return flat.slice(0, STUDIO_SWATCH_LIMIT);
    const tags = RECOMMENDED_TAGS[activeSurface] || [];
    const scored = flat.map((c) => {
      let score = 0;
      const ctags = c.tags || [];
      for (const t of tags) if (ctags.includes(t)) score += 3;
      if (c.designer_pick) score += 1;
      if (c.popular) score += 1;
      return { c, score };
    }).filter((x) => x.score > 0);
    scored.sort((a, b) => b.score - a.score);
    const out = scored.slice(0, STUDIO_SWATCH_LIMIT).map((x) => x.c);
    if (out.length < STUDIO_SWATCH_LIMIT) {
      for (const c of flat) {
        if (out.length >= STUDIO_SWATCH_LIMIT) break;
        if (!out.includes(c) && (c.designer_pick || c.popular)) out.push(c);
      }
    }
    return out;
  }, [activeSurface, flat]);

  // Base pool for whichever view the customer is in.
  const basePool = useMemo(() => {
    if (view === "favourites") {
      return flat.filter((c) => favourites.includes(c.hex.toUpperCase()));
    }
    return recommended;
  }, [view, recommended, flat, favourites]);

  // Search narrows the current pool. Search only over the compact pool +
  // pinned exact matches from the full library — the full 1 041 browsing
  // experience lives at /colours.
  const visibleColours = useMemo(() => {
    if (!q.trim()) return basePool.slice(0, STUDIO_SWATCH_LIMIT);
    const r = searchPalette(q, flat);
    const seen = new Set();
    const out = [];
    if (r.pinned) { out.push(r.pinned.colour); seen.add(r.pinned.colour.hex.toUpperCase()); }
    for (const c of r.directMatches) {
      const h = c.hex.toUpperCase();
      if (!seen.has(h)) { out.push(c); seen.add(h); }
      if (out.length >= STUDIO_SWATCH_LIMIT) break;
    }
    if (out.length === 0) {
      const n = q.trim().toLowerCase();
      for (const c of flat) {
        if (out.length >= STUDIO_SWATCH_LIMIT) break;
        if (c.name.toLowerCase().includes(n) || c.hex.toLowerCase().includes(n)) out.push(c);
      }
    }
    return out;
  }, [q, basePool, flat]);

  const browseAllHref = `/colours?from=workspace&project=${projectId || ""}${activeArea ? `&area=${activeArea}` : ""}${activePartSlug ? `&part=${activePartSlug}` : ""}${q.trim() ? `&q=${encodeURIComponent(q.trim())}` : ""}`;

  return (
    <div
      className="bg-white rounded-3xl shadow-soft border border-brand-border flex flex-col shrink-0"
      data-testid="colour-studio"
    >
      {/* Studio header — current part + current colour */}
      <div className="px-6 pt-5 pb-4 border-b border-brand-border shrink-0" data-testid="studio-header">
        <div className="flex items-center justify-between gap-3 mb-1">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-muted">Colour picker</div>
          {projectId && (
            <Link
              to={browseAllHref}
              data-testid="studio-browse-all-colours"
              className="text-[11px] font-semibold text-brand-teal hover:text-brand-ink transition-colors"
            >
              Browse all {totalLibraryCount.toLocaleString()} colours →
            </Link>
          )}
        </div>
        <h2 className="text-xl font-bold text-brand-ink font-serif" data-testid="studio-surface-label">
          {activeSurface ? surfaceLabel(activeSurface) : "Pick a part of your home"}
        </h2>
        <div className="mt-3 flex items-center gap-3 rounded-2xl bg-brand-linen p-2.5" data-testid="studio-current-colour">
          <div className="w-10 h-10 rounded-xl border-2 border-white shadow-soft shrink-0"
               style={{ background: currentColour?.color_hex || "repeating-linear-gradient(45deg,#e7e3d8,#e7e3d8 6px,#f5f1e8 6px,#f5f1e8 12px)" }} />
          <div className="min-w-0 flex-1">
            <div className="text-[9px] uppercase tracking-widest text-brand-muted font-semibold">Current colour</div>
            <div className="text-sm font-bold text-brand-ink truncate font-serif">
              {currentColour?.color_name || "None yet"}
            </div>
          </div>
        </div>
      </div>

      {/* View toggle: Recommended · Favourites (search bar sits below) */}
      <div className="px-6 pt-3 pb-2 shrink-0 flex items-center gap-2" data-testid="studio-view-toggle">
        <button
          onClick={() => setView("recommended")}
          data-testid="studio-view-recommended"
          className={`h-8 px-3.5 rounded-full text-[11px] font-semibold border transition-colors ${
            view === "recommended" && !q.trim()
              ? "bg-brand-ink text-white border-brand-ink"
              : "bg-white text-brand-ink border-brand-border hover:border-brand-ink/40"
          }`}
        >
          Recommended
        </button>
        <button
          onClick={() => setView("favourites")}
          data-testid="studio-view-favourites"
          className={`h-8 px-3.5 rounded-full text-[11px] font-semibold border transition-colors ${
            view === "favourites" && !q.trim()
              ? "bg-brand-ink text-white border-brand-ink"
              : "bg-white text-brand-ink border-brand-border hover:border-brand-ink/40"
          }`}
        >
          Favourites <span className="ml-1 text-brand-muted">({favourites.length})</span>
        </button>
      </div>

      {/* Search — always visible */}
      <div className="px-6 pb-3 shrink-0">
        <div className="relative">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-brand-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search paint names or codes"
            data-testid="studio-search"
            className="w-full h-10 pl-10 pr-3 rounded-2xl bg-brand-linen border border-transparent text-sm placeholder:text-brand-muted focus:outline-none focus:bg-white focus:border-brand-teal focus:ring-4 focus:ring-brand-teal/10 transition-all"
          />
        </div>
      </div>

      {/* Section heading — compact */}
      <div className="px-6 pb-2 shrink-0 flex items-baseline justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-muted">
          {q.trim()
            ? "Search results"
            : view === "favourites"
              ? "Your favourites"
              : `Recommended for ${activeSurface ? surfaceLabel(activeSurface) : "this part"}`}
        </div>
        <div className="text-[10px] text-brand-muted">Showing {visibleColours.length}</div>
      </div>

      {/* Swatch grid — capped at STUDIO_SWATCH_LIMIT so the wall never
          buries the Apply CTA (founder-locked UX guardrail 2026-02-24). */}
      <div className="px-6 py-2" data-testid="studio-swatches">
        <div className="grid grid-cols-3 gap-3">
          {visibleColours.map((c) => {
            const hexUp = c.hex.toUpperCase();
            const isSel = selected?.hex?.toUpperCase() === hexUp;
            const isCurrent = currentColour?.color_hex?.toUpperCase() === hexUp;
            const isFav = favourites.includes(hexUp);
            return (
              <div key={`${c.hex}-${c.name}`} className="group relative">
                <button
                  onClick={() => setSelected(c)}
                  data-testid={`studio-swatch-${c.hex.replace("#", "")}`}
                  className="w-full text-left"
                  title={`${c.name} · ${c.hex}`}
                >
                  <div
                    className={`w-full aspect-square rounded-xl border-2 transition-all ${
                      isSel ? "border-brand-teal ring-4 ring-brand-teal/20 shadow-navy scale-[1.03]"
                      : isCurrent ? "border-brand-ink"
                      : "border-white shadow-soft group-hover:shadow-navy group-hover:scale-[1.02]"
                    }`}
                    style={{ background: c.hex }}
                  />
                  <div className="mt-1.5 px-0.5">
                    <div className={`text-[11px] leading-tight font-serif truncate ${isSel ? "font-bold text-brand-teal" : "font-semibold text-brand-ink"}`}>
                      {c.name}
                    </div>
                    <div className="text-[9px] text-brand-muted uppercase tracking-widest mt-0.5 truncate">
                      {c.code || c.hex}
                    </div>
                  </div>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); toggleFavourite(c.hex); }}
                  data-testid={`studio-fav-${c.hex.replace("#", "")}`}
                  title={isFav ? "Remove from favourites" : "Save to favourites"}
                  className={`absolute top-1.5 right-1.5 w-6 h-6 rounded-full inline-flex items-center justify-center transition-all ${
                    isFav ? "bg-brand-navy text-white opacity-100"
                    : "bg-white/85 text-brand-muted opacity-0 group-hover:opacity-100 hover:text-brand-navy"
                  }`}
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill={isFav ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                  </svg>
                </button>
              </div>
            );
          })}
          {visibleColours.length === 0 && (
            <div className="col-span-3 text-center text-brand-muted text-sm py-8" data-testid="studio-empty-state">
              {view === "favourites" && !q.trim()
                ? "No favourites yet. Star any swatch to save it here."
                : q.trim()
                  ? <>No colours match &ldquo;{q}&rdquo; here. Try <Link to={browseAllHref} className="font-semibold text-brand-teal hover:underline">Browse all colours</Link>.</>
                  : "Nothing to show yet."}
            </div>
          )}
        </div>
        {/* Doorway to the full 1 041-colour library — always visible below
            the compact grid so customers know where to go next. */}
        <div className="mt-3 text-center">
          <Link
            to={browseAllHref}
            data-testid="studio-browse-all-inline"
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-teal hover:text-brand-ink"
          >
            Not what you&apos;re after? Browse all {totalLibraryCount.toLocaleString()} colours →
          </Link>
        </div>
      </div>

      {/* Apply — sticky at the bottom of the outer scroll surface so it is
          always in reach even before the customer scrolls to the end of the
          studio (founder-locked UX guardrail 2026-02-24). */}
      <div className="px-6 py-4 border-t border-brand-border shrink-0 bg-white sticky bottom-0 z-10 rounded-b-3xl" data-testid="studio-apply-bar">
        <button
          onClick={() => selected && handleApply(selected)}
          disabled={!selected}
          data-testid="studio-apply-btn"
          className="w-full h-12 rounded-2xl bg-brand-navy text-white font-bold text-sm inline-flex items-center justify-center gap-2 hover:bg-brand-teal disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-navy hover:shadow-lg"
        >
          <Check size={15} />
          {selected
            ? (applyLabelOverride
                ? `Apply ${selected.name} · ${applyLabelOverride}`
                : `Apply ${selected.name} to ${activeSurface ? surfaceLabel(activeSurface) : "your home"}`)
            : (applyLabelOverride || "Pick a colour to apply")}
        </button>
        <p className="text-[11px] text-brand-muted text-center mt-2 leading-relaxed">
          Happy with your colours? Hit <span className="font-semibold text-brand-ink">Render Scheme</span> in the header.
        </p>
      </div>
    </div>
  );
}


// ─── Filter primitives ─────────────────────────────────────────
function FilterRow({ label, children }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-brand-muted shrink-0 min-w-[52px]">{label}</span>
      <div className="flex-1 flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}
function FilterChip({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`h-7 px-3 rounded-full text-[11px] font-semibold transition-colors border ${
        active ? "bg-brand-ink text-white border-brand-ink"
          : "bg-white text-brand-ink border-brand-border hover:border-brand-ink/40 hover:bg-brand-linen"
      }`}
    >
      {children}
    </button>
  );
}

// ─── All Surfaces modal ─────────────────────────────────────────
function AllSurfacesModal({ catalogue, scope, exclude, onClose, onPick }) {
  const [q, setQ] = useState("");
  const listRef = useRef(null);
  const onWheel = useWheelForwardY(listRef);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (catalogue || [])
      .filter((g) => g.category === scope || g.category === "both")
      .map((g) => ({
        ...g,
        items: (g.items || []).filter((it) =>
          !s || it.display_name.toLowerCase().includes(s) || it.slug.toLowerCase().includes(s),
        ),
      }))
      .filter((g) => g.items.length > 0);
  }, [catalogue, scope, q]);

  return (
    <div className="fixed inset-0 z-50 bg-brand-ink/60 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="all-surfaces-modal">
      <div
        className="bg-white rounded-3xl shadow-navy w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onWheel={onWheel}
      >
        <div className="px-6 py-5 border-b border-brand-border flex items-center gap-3">
          <div className="flex-1">
            <h2 className="text-lg font-bold text-brand-ink font-serif">Advanced Surfaces</h2>
            <p className="text-xs text-brand-muted mt-0.5">Anything beyond the essentials. Pick one to add to your scheme.</p>
          </div>
          <button onClick={onClose} data-testid="all-surfaces-close"
            className="w-9 h-9 rounded-full hover:bg-brand-linen inline-flex items-center justify-center text-brand-muted">
            <X size={16} />
          </button>
        </div>
        <div className="px-6 py-4 border-b border-brand-border">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search surfaces…" data-testid="all-surfaces-search"
              className="w-full h-10 pl-9 pr-3 rounded-xl border border-brand-border text-sm focus:outline-none focus:ring-2 focus:ring-brand-teal/30" />
          </div>
        </div>
        <div ref={listRef} className="flex-1 overflow-y-auto cm-scroll cm-scroll-fade px-6 py-4 space-y-5">
          {filtered.map((g) => (
            <div key={g.subcategory} data-testid={`all-surfaces-group-${g.subcategory}`}>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-muted mb-2.5">
                {g.subcategory.replace(/_/g, " ")}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {g.items.map((it) => {
                  const already = exclude.has(it.slug);
                  return (
                    <button key={it.slug} disabled={already} onClick={() => onPick(it.slug)}
                      data-testid={`all-surfaces-pick-${it.slug}`}
                      className={`text-left rounded-xl border px-3.5 py-2.5 text-sm transition-all ${
                        already ? "border-brand-border bg-brand-linen text-brand-muted cursor-not-allowed"
                          : "border-brand-border hover:border-brand-teal hover:bg-brand-linen hover:shadow-soft"
                      }`}>
                      <div className="font-semibold text-brand-ink truncate">{it.display_name}</div>
                      <div className="text-[10px] text-brand-muted mt-0.5">{already ? "Already in your scheme" : "Add to scheme"}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="text-center text-brand-muted text-sm py-10">No surfaces match &ldquo;{q}&rdquo;</div>
          )}
        </div>
      </div>
    </div>
  );
}

function HelpOverlay({ onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-brand-ink/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-navy w-full max-w-md p-7" onClick={(e) => e.stopPropagation()} data-testid="ws-help-overlay">
        <h2 className="text-xl font-bold text-brand-ink mb-4 font-serif">How this works</h2>
        <ol className="space-y-3.5 text-sm text-brand-muted">
          <li className="flex gap-3"><span className="font-serif font-bold text-brand-teal">1.</span> Tap a surface at the top of your scheme (Walls, Trim, Fascia, Door, Roof).</li>
          <li className="flex gap-3"><span className="font-serif font-bold text-brand-teal">2.</span> Pick a colour on the right and hit Apply.</li>
          <li className="flex gap-3"><span className="font-serif font-bold text-brand-teal">3.</span> Repeat for each surface you&rsquo;d like to change.</li>
          <li className="flex gap-3"><span className="font-serif font-bold text-brand-teal">4.</span> Hit Render Scheme to see them all together.</li>
          <li className="flex gap-3"><span className="font-serif font-bold text-brand-teal">5.</span> Every render is saved. Click a version in Your Saved Renders to compare.</li>
        </ol>
        <button onClick={onClose} data-testid="ws-help-close"
          className="mt-6 w-full h-11 rounded-2xl bg-brand-navy text-white text-sm font-semibold hover:bg-brand-teal transition-colors">
          Got it
        </button>
      </div>
    </div>
  );
}
